import { travelHorizonMessage } from '@massilia/contracts';
import { format } from 'date-fns';
import { z } from 'zod';
import { DEFAULT_OPTION, genderTypes } from './base-schemas';
import { EMPTY_FLIGHT_LEG, multiDestinationFlightEstimateSchema, oneWayFlightEstimateSchema, roundTripFlightEstimateSchema, } from './flight-schemas';
import { tourismEstimateSchema } from './tourism-schemas';
export const estimateFormSchema = z.union([
    oneWayFlightEstimateSchema,
    roundTripFlightEstimateSchema,
    multiDestinationFlightEstimateSchema,
    tourismEstimateSchema,
]);
export function getEstimateFormSchema(type, opts) {
    return withTravelHorizon(getBaseEstimateFormSchema(type), opts?.maxTravelDate);
}
export function makeEstimateFormSchema(opts) {
    return withTravelHorizon(estimateFormSchema, opts?.maxTravelDate);
}
/** Les dates du formulaire sont des chaînes `yyyy-MM-dd` : la comparaison est lexicale. */
function addTravelHorizonIssues(data, ctx, maxTravelDate) {
    const maxDateStr = format(maxTravelDate, 'yyyy-MM-dd');
    const message = travelHorizonMessage();
    if (data.estimateType === 'FLIGHT') {
        data.flights?.forEach((leg, index) => {
            if (leg.date && leg.date > maxDateStr) {
                ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: ['flights', index, 'date'] });
            }
        });
        return;
    }
    for (const key of ['departureDate', 'returnDate']) {
        const value = data[key];
        if (value && value > maxDateStr) {
            ctx.addIssue({ code: z.ZodIssueCode.custom, message, path: [key] });
        }
    }
}
function getBaseEstimateFormSchema(type) {
    switch (type) {
        case 'CRUISE':
        case 'STAY':
        case 'TOUR':
            return tourismEstimateSchema;
        case 'MULTI_DESTINATION':
            return multiDestinationFlightEstimateSchema;
        case 'ONE_WAY':
            return oneWayFlightEstimateSchema;
        case 'ROUND_TRIP':
            return roundTripFlightEstimateSchema;
        default:
            throw new Error(`Unknown estimate type: ${type}`);
    }
}
function withTravelHorizon(schema, maxTravelDate) {
    if (!maxTravelDate)
        return schema;
    return schema.superRefine((data, ctx) => addTravelHorizonIssues(data, ctx, maxTravelDate));
}
export const ESTIMATE_FORM_DEFAULT_VALUES = {
    // Start as FLIGHT type by default
    estimateType: 'FLIGHT',
    // Common fields
    clientFirstName: '',
    clientLastName: '',
    contactEmail: '',
    complementaryInformation: '',
    contactPhoneNumber: '',
    existingPassengerIds: [],
    maxStayDuration: 0,
    newPassengers: [
        {
            firstName: '',
            lastName: '',
            birthDate: '',
            gender: genderTypes.male,
            nationality: DEFAULT_OPTION,
        },
    ],
    travelClass: 'ECONOMY',
    // FLIGHT-only fields (active when estimateType === 'FLIGHT')
    companyPreference: { companyName: DEFAULT_OPTION, hasPreference: false },
    flights: [{ ...EMPTY_FLIGHT_LEG }, { ...EMPTY_FLIGHT_LEG }],
    particularities: '',
    // type field: ROUND_TRIP for flights (consistent with 2 flight legs)
    type: 'ROUND_TRIP',
    // TOURISM-only fields (active when estimateType === 'TOURISM')
    // These are present but ignored when estimateType === 'FLIGHT'
    approximativeBudget: 0,
    carLoan: false,
    departure: DEFAULT_OPTION,
    departureDate: '',
    departureFlexibilityAfter: 0,
    departureFlexibilityBefore: 0,
    destination: DEFAULT_OPTION,
    hotelMinimumNumberOfStars: null,
    onSiteActivities: false,
    pension: 'ROOM_ONLY',
    returnDate: '',
    returnFlexibilityAfter: 0,
    returnFlexibilityBefore: 0,
    transfertFromAirport: false,
};
